/**
 *
 * IBM Confidential
 *
 * (C) Copyright IBM Corp. 2021, 2025
 *
 * The source code for this program is not published or otherwise
 * divested of its trade secrets, irrespective of what has been
 * deposited with the U. S. Copyright Office
 *
 * US Government Users Restricted Rights - Use, duplication or
 * disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 *
 **/

import { defineMessages } from 'react-intl';

export const messages = defineMessages({
  title: {
    id: 'searchDetailsConfig.title',
    defaultMessage: 'Search configuration'
  },
  description: {
    id: 'searchDetailsConfig.description',
    defaultMessage: 'To test this, go to the '
  },
  descriptionLink: {
    id: 'searchDetailsConfig.description',
    defaultMessage: 'evaluations page'
  },
  restoreDefault: {
    id: 'searchDetailsConfig.restoreDefault',
    defaultMessage: 'Restore default'
  },
  intro: {
    id: 'searchDetailsConfig.intro',
    defaultMessage:
      'Troubleshoot and improve the answers generated by your search integration.'
  },
  fieldDefaultExample: {
    id: 'searchDetailsConfig.fieldDefaultExample',
    defaultMessage: 'No data found'
  },
  collectionNoDocsAvailable: {
    id: 'searchDetailsConfig.collectionNoDocsAvailable',
    defaultMessage: 'There is no available document in the collection.'
  },
  configTitle: {
    id: 'searchDetailsConfig.configTitle',
    defaultMessage: 'Configure result content'
  },
  discoInstance: {
    id: 'searchDetailsConfig.discoInstance',
    defaultMessage: 'Discovery instance: '
  },
  discoCollection: {
    id: 'searchDetailsConfig.discoCollection',
    defaultMessage: 'Collection: '
  },
  discoProject: {
    id: 'searchDetailsConfig.discoProject',
    defaultMessage: 'Project: '
  },
  openInDiscovery: {
    id: 'searchDetailsConfig.openInDiscovery',
    defaultMessage: 'Open in Discovery'
  },
  learnMore: {
    id: 'searchDetailsConfig.learnMore',
    defaultMessage: 'Learn more'
  },
  results: {
    id: 'searchDetailsConfig.results',
    defaultMessage: 'results'
  },
  resultsQtyTitle: {
    id: 'searchDetailsConfig.resultsQtyTitle',
    defaultMessage: 'Adjust result quantity'
  },
  resultsQtyShowTopResults: {
    id: 'searchDetailsConfig.resultsQtyShowTopResults',
    defaultMessage: 'Show the top'
  },
  resultsQtyShowMoreLink: {
    id: 'searchDetailsConfig.resultsQtyShowMoreLink',
    defaultMessage: 'Include link for customers to view up to 10 results'
  },
  invalidNum: {
    id: 'searchDetailsConfig.invalidNum',
    defaultMessage: 'Enter a valid number (1 to 5)'
  },
  conversationalSearch: {
    id: 'searchDetailsConfig.conversationalSearch',
    defaultMessage: 'Conversational search'
  },
  conversationalSearchTitle: {
    id: 'searchDetailsConfig.conversationalSearchTitle',
    defaultMessage:
      'Use a watsonx generative AI model hosted in Dallas, TX US, to generate conversational responses.'
  },
  conversationalSearchTitleFrankfurt: {
    id: 'searchDetailsConfig.conversationalSearchTitleFrankfurt',
    defaultMessage:
      'Use a watsonx generative AI model hosted in Frankfurt, GER, to generate conversational responses.'
  },
  conversationalSearchTitleCPD: {
    id: 'searchDetailsConfig.conversationalSearchTitle',
    defaultMessage:
      'Use a watsonx generative AI model to generate conversational responses.'
  },
  conversationalSearchLabel: {
    id: 'searchDetailsConfig.conversationalSearchLabel',
    defaultMessage: 'Toggle for conversational search'
  },
  conversationalSearchCitationsTitleLabel: {
    id: 'searchDetailsConfig.conversationalSearchCitationsTitleLabel',
    defaultMessage: 'Define the text for the citations title.'
  },
  conversationalSearchCitationsTitleTooltip: {
    id: 'searchDetailsConfig.conversationalSearchCitationsTitleTooltip',
    defaultMessage:
      'The citations title will be shown on all integrations except for the web chat integration.'
  },
  conversationalSearchCitationsTitlePlaceholder: {
    id: 'searchDetailsConfig.conversationalSearchCitationsTitlePlaceholder',
    defaultMessage: 'How do we know?'
  },
  conversationalSearchCitationsTitleInvalidEmptyValueText: {
    id: 'searchDetailsConfig.conversationalSearchCitationsTitleInvalidEmptyValueText',
    defaultMessage: `Value can't be empty.`
  },
  conversationalSearchInlineWarningTitle: {
    id: 'searchDetailsConfig.conversationalSearchInlineWarningTitle',
    defaultMessage: `Conversational search is on`
  },
  conversationalSearchInlineWarningSubtitle: {
    id: 'searchDetailsConfig.conversationalSearchInlineWarningSubtitle',
    defaultMessage: `The following options are not enabled unless conversational search is turned off.`
  },
  conversationalSearchConfidenceTitle: {
    id: 'searchDetailsConfig.conversationalSearchConfidenceTitle',
    defaultMessage: `Tendency to say "I don't know"`
  },
  conversationalSearchRetrievalConfidenceTitle: {
    id: 'searchDetailsConfig.conversationalSearchConfidenceTitle',
    defaultMessage: 'Retrieval confidence threshold'
  },
  conversationalSearchResponseTitle: {
    id: 'searchDetailsConfig.conversationalSearchResponseTitle',
    defaultMessage: 'Generated response length'
  },
  conversationalSearchResponseConfidenceTitle: {
    id: 'searchDetailsConfig.conversationalSearchResponseConfidenceTitle',
    defaultMessage: 'Response confidence threshold'
  },
  conversationalSearchRetrievalConfidenceTooltip: {
    id: 'searchDetailsConfig.conversationalSearchRetrievalConfidenceTooltip',
    defaultMessage:
      'Before response is generated, this setting checks the confidence of the retrieved citations.'
  },
  conversationalSearchResponseLengthTooltip: {
    id: 'searchDetailsConfig.conversationalSearchResponseLengthTooltip',
    defaultMessage:
      'This setting affects the average response length. Depending on user input, variations from the selected length may occur. '
  },
  conversationalSearchResponseConfidenceTooltip: {
    id: 'searchDetailsConfig.conversationalSearchResponseConfidenceTooltip',
    defaultMessage:
      'After response is generated, this setting checks the confidence of the generated citations.'
  },
  conversationalSearchResponseConcise: {
    id: 'searchDetailsConfig.conversationalSearchResponseConcise',
    defaultMessage: 'Concise'
  },
  conversationalSearchResponseModerate: {
    id: 'searchDetailsConfig.conversationalSearchResponseModerate',
    defaultMessage: 'Moderate'
  },
  conversationalSearchResponseVerbose: {
    id: 'searchDetailsConfig.conversationalSearchResponseVerbose',
    defaultMessage: 'Verbose'
  },
  conversationalSearchConfidenceRarely: {
    id: 'searchDetailsConfig.conversationalSearchConfidenceRarely',
    defaultMessage: 'Rarely'
  },
  conversationalSearchConfidenceLessOften: {
    id: 'searchDetailsConfig.conversationalSearchConfidenceLessOften',
    defaultMessage: 'Less often'
  },
  conversationalSearchConfidenceMoreOften: {
    id: 'searchDetailsConfig.conversationalSearchConfidenceMoreOften',
    defaultMessage: 'More often'
  },
  conversationalSearchConfidenceMostOften: {
    id: 'searchDetailsConfig.conversationalSearchConfidenceMostOften',
    defaultMessage: 'Most often'
  },
  conversationalSearchConfidenceUsually: {
    id: 'searchDetailsConfig.conversationalSearchConfidenceUsually',
    defaultMessage: 'Usually'
  },
  lowest: {
    id: 'searchDetailsConfig.lowest',
    defaultMessage: 'Lowest'
  },
  low: {
    id: 'searchDetailsConfig.low',
    defaultMessage: 'Low'
  },
  high: {
    id: 'searchDetailsConfig.high',
    defaultMessage: 'High'
  },
  highest: {
    id: 'searchDetailsConfig.highest',
    defaultMessage: 'Highest'
  },
  emphasizeAnswers: {
    id: 'searchDetailsConfig.emphasizeAnswers',
    defaultMessage: 'Emphasize the answer'
  },
  emphasizeAnswersTitle: {
    id: 'searchDetailsConfig.emphasizeAnswersTitle',
    defaultMessage: 'Extract the answers and emphasize it in the results card'
  },
  emphasizeAnswersAriaLabel: {
    id: 'searchDetailsConfig.emphasizeAnswersAriaLabel',
    defaultMessage: 'Toggle for emphasize answers'
  },
  emphasizeAnswersToolTip: {
    id: 'searchDetailsConfig.emphasizeAnswersToolTip',
    defaultMessage: `Switching this toggle "On" will extract, when found, a concise answer to the user's question from the result's Body's text. If using the web chat integration, this answer will be displayed at the top of the result's card and will get highlighted in the text`
  },
  refineResultsHeader: {
    id: 'searchDetailsConfig.refineResultsHeader',
    defaultMessage: 'Set result selectivity'
  },
  refineResultsTitle: {
    id: 'searchDetailsConfig.refineResultsTitle',
    defaultMessage: 'Refine results to return more selective answers'
  },
  refineResultsAriaLabel: {
    id: 'searchDetailsConfig.refineResultsAriaLabel',
    defaultMessage: 'Toggle for refined results'
  },
  refineResultsToolTip: {
    id: 'searchDetailsConfig.refineResultsToolTip',
    defaultMessage:
      'Switching this toggle "On" will return more selective results. Test some common searches in the "Try it out" pane to preview the result quality.'
  },
  applied: {
    id: 'searchDetailsConfig.applied',
    defaultMessage: 'Applied in Discovery service'
  },
  loading: {
    id: 'searchDetailsConfig.loading',
    defaultMessage: 'Loading...'
  },
  example: {
    id: 'searchDetailsConfig.example',
    defaultMessage: 'Example: '
  },
  notAvailable: {
    id: 'searchDetailsConfig.notAvailable',
    defaultMessage: 'Not Available'
  },
  editDiscovery: {
    id: 'searchDetailsConfig.editDiscovery',
    defaultMessage: 'Edit Discovery Settings'
  },
  pricingAndTermsInfo: {
    id: 'ConversationalSearchMessages.pricingAndTermsInfo',
    defaultMessage:
      'By using this feature you agree to the {PricingLink} and {TermsLink}. This feature is not PCI validated. The use of this feature may impact your PCI compliance.'
  },
  conversationTurns: {
    id: 'ConversationalSearchMessages.conversationalTurns',
    defaultMessage: 'Contextual awareness'
  },
  conversationSingleTurn: {
    id: 'ConversationalSearchMessages.conversationSingleTurn',
    defaultMessage: '<strong>Single turn</strong>'
  },
  conversationSingleTurnDescription: {
    id: 'ConversationalSearchMessages.conversationSingleTurnDescription',
    defaultMessage:
      'The assistant uses only the current user input for retrieving search results and generating answers. This works well for clear, complete inputs but generally won’t work with context-dependent queries such as, "Why is that?" after a previous answer.'
  },
  conversationMultiTurn: {
    id: 'ConversationalSearchMessages.conversationMultiTurn',
    defaultMessage: '<strong>Entire conversation</strong>'
  },
  conversationMultiTurnDescription: {
    id: 'ConversationalSearchMessages.conversationMultiTurnDescription',
    defaultMessage:
      'The assistant uses the entire session history for retrieving search results and generating answers. This handles context-dependent questions well but may over-rely on past topics, even if the user has moved on.'
  },
  citations: {
    id: 'ConversationalSearchMessages.citations',
    defaultMessage: 'Citations'
  },
  citationsDescription: {
    id: 'ConversationalSearchMessages.citationsDescription',
    defaultMessage:
      "Citations will be displayed to the end user. This doesn't impact the number of citations used in the response."
  },
  citationsLabel: {
    id: 'ConversationalSearchMessages.citationsLabel',
    defaultMessage: 'Citations shown in web chat'
  },
  citationsAll: {
    id: 'ConversationalSearchMessages.citationsAll',
    defaultMessage: 'All'
  },
  citations1: {
    id: 'ConversationalSearchMessages.citations1',
    defaultMessage: '1'
  },
  citations2: {
    id: 'ConversationalSearchMessages.citations2',
    defaultMessage: '2'
  },
  citations3: {
    id: 'ConversationalSearchMessages.citations3',
    defaultMessage: '3'
  },
  citationsNone: {
    id: 'ConversationalSearchMessages.citationsNone',
    defaultMessage: 'None'
  }
});
